import { ApiClientError } from '@hb/api-client';
import { computed, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { errorMessageKey } from './errors.js';
/**
 * One submit button's worth of state: in flight, failed, and why.
 *
 * The translation happens here rather than in each view because the API never
 * sends a sentence - it sends a stable code, and the wording is ours. Doing it
 * once means a new error code needs a catalogue entry and nothing else.
 *
 * It lives in this package rather than in `libs/ui` because that is where the
 * only interesting thing it does already lives: {@link errorMessageKey} is the
 * bridge from an API error code to a sentence, and this is its ergonomic form.
 * `libs/ui` is deliberately translation-free and knows nothing about the API.
 * The pending flag rides along because splitting the two would make every call
 * site assemble the halves itself.
 */
export function useAsyncAction() {
    const { t } = useI18n();
    const pending = ref(false);
    const error = shallowRef(null);
    function clear() {
        error.value = null;
    }
    return {
        pending,
        failed: computed(() => error.value !== null),
        code: computed(() => error.value?.code ?? null),
        message: computed(() => {
            const current = error.value;
            if (current === null)
                return '';
            return t(errorMessageKey(current.code), { ...current.params });
        }),
        fieldErrors: computed(() => error.value?.fieldErrors ?? {}),
        clear,
        async run(action) {
            pending.value = true;
            clear();
            try {
                return await action();
            }
            catch (cause) {
                // Anything that is not already an ApiClientError came from our own code
                // rather than from the API, and gets the generic wording - it must not
                // reach a user as a stack trace.
                error.value = cause instanceof ApiClientError ? cause : new ApiClientError({ code: 'unknown', status: 0, message: String(cause), cause });
                return undefined;
            }
            finally {
                pending.value = false;
            }
        },
    };
}
//# sourceMappingURL=useAsyncAction.js.map